import YouTubePlayer from 'youtube-player';
import UiModal from '../UiModal/UiModal';
import { createElement } from '../../utils/render';
import { createUiVideoModalTemplate } from './ui-video-modal.template';
import { updateTimeToStart, playVideo, setMute } from './utils';

const defaultClasses = Object.freeze({
  modal: 'ui-video-modal',
  close: 'ui-video-modal__close',
  leave: 'ui-video-modal-leave-to'
});

const defaultOptions = Object.freeze({
  root: document.body,
  video: null,
  playerType: 'youtube',
  theme: 'horizontal',
  isNeedTrackForGTM: false
});

export class UiVideoModal extends UiModal {
  constructor(options) {
    super(null, options, defaultClasses);

    this._options = {
      ...defaultOptions,
      ...options
    }

    this.player = null;
    this.interval = null;
    this.isPlayingVideo = false;

    //  only vk
    this.currentTime = 0;
    this.duration = 0;
    this.settings = {
      currentTime: 0,
      duration: 0,
      mute: false,
      autoPlay: true,
      unfocusable: false
    };
    this.iframeMessageHandler = null
  }

  get template() {
    return createUiVideoModalTemplate(this._options.theme);
  }

  beforeMounted() {
    switch (this._options.playerType) {
      case 'youtube': {
        this.createYouTubePlayer(this._options.video);
        break
      }
      case 'vk': {
        this.createVkPlayer(this._options.video);
        break
      }
      case 'video': {
        const videoData = JSON.parse(this._options.video);

        this.createVideoPlayer(videoData);
        break
      }
    }
  }

  beforeUnmounted() {
    switch (this._options.playerType) {
      case 'youtube':
        this.destroyYouTubePlayer();
        break
      case 'vk':
        this.destroyVkPlayer();
        break
      case 'video':
        this.destroyVideoPlayer();
        break
    }
  }

  async createObserveYouTubeVideo() {
    const timeToSendEvent = await this.player.getDuration() * 0.9;

    this.interval = setInterval(async () => {
      if (this.isPlayingVideo) {
        const currentTime = await this.player.getCurrentTime();

        if (currentTime >= timeToSendEvent) {
          window.$analytics && window.$analytics.push('video-watched-90-percent');
          this.removeObserveYouTubeVideo(true);
        }
      }
    }, 1000);
  }

  removeObserveYouTubeVideo(isPlay = false) {
    this.isPlayingVideo = isPlay;
    clearInterval(this.interval);
  }

  createYouTubePlayer(id) {
    this.player = YouTubePlayer(this.contentWrapper.firstElementChild);
    this.player.loadVideoById(id);
    this.player.playVideo();

    if (this._options.isNeedTrackForGTM) {
      this.player.on('stateChange', async ({ data }) => {
        if (data === 1 && !this.isPlayingVideo) {
          this.isPlayingVideo = true;
          await this.createObserveYouTubeVideo(this.player);
        }
      });
    }
  }

  destroyYouTubePlayer() {
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    this.removeObserveYouTubeVideo();
  }

  createObserveVkVideo() {
    let { mute, autoPlay, currentTime, duration } = this.settings;

    return (event) => {
      if (this.player && (event.source === this.player.contentWindow)) {
        const data = event.data;

        switch (data.event) {
          case 'inited': {
            updateTimeToStart(this.player);
            setMute(mute, this.player);

            if (autoPlay) {
              playVideo(this.player);
            }

            duration = data.duration;
            currentTime = data.time;

            break;
          }
          case 'timeupdate': {
            currentTime = data.time;

            if (this._options.isNeedTrackForGTM && (currentTime >= duration * 0.9)) {
              window.$analytics && window.$analytics.push('video-watched-90-percent');
              this._options.isNeedTrackForGTM = false;
            }
            break;
          }
          case 'started':
          case 'resumed': {
            currentTime = data.time;
            break;
          }
        }
      }
    };
  }

  removeObserveVkVideo() {
    window.removeEventListener('message', this.iframeMessageHandler);
    this.iframeMessageHandler = null;
  }

  createVkPlayer(id) {
    const template = `<iframe
      src="https://vk.com/video_ext.php?${id}&hd=2&origin=${window.location.origin}&js_api=1"
      width="360"
      height="640"
      frameborder="0"
      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
    >
    </iframe>`;

    this.player = createElement(template);

    this.iframeMessageHandler = this.createObserveVkVideo();
    this.contentWrapper.replaceChild(this.player, this.contentWrapper.firstElementChild);

    window.addEventListener('message', this.iframeMessageHandler);
  }

  destroyVkPlayer() {
    if (this.player) {
      this.player.remove();
      this.player = null;
    }

    this.removeObserveVkVideo();
  }

  createObserveVideo() {
    const timeToSendEvent = this.player.duration * 0.9;

    this.interval = setInterval(() => {
      if (this.isPlayingVideo) {
        const currentTime = this.player.currentTime;

        if (currentTime >= timeToSendEvent) {
          window.$analytics && window.$analytics.push('video-watched-90-percent');
          this.removeObserveVideo(true);
        }
      }
    }, 1000);
  }

  removeObserveVideo(isPlay = false) {
    this.isPlayingVideo = isPlay;
    clearInterval(this.interval);
  }

  createVideoPlayer(videoData) {
    const template = `<video
        poster="${videoData.poster}"
        controlsList="nodownload"
        controls
        autoplay
        playsinline
      >
        <source src="${videoData.webm}" type="video/webm">
        <source src="${videoData.mp4}" type="video/mp4">
    </video>`;

    this.player = createElement(template);
    this.contentWrapper.replaceChild(this.player, this.contentWrapper.firstElementChild);

    if (this._options.isNeedTrackForGTM) {
      this.player.addEventListener('play', () => {
        if (!this.isPlayingVideo) {
          this.createObserveVideo(this.player);
          this.isPlayingVideo = true;
        }
      })
    }
  }

  destroyVideoPlayer() {
    if (this.player) {
      this.player.remove();
      this.player = null;
    }

    this.removeObserveVideo();
  }
}
